import { type DropDownItem } from "../../pojo/DropDownItem";
import { Item } from "../../pojo/Item";
import { S25ItemI } from "../../pojo/S25ItemI";
import { SearchCriteria } from "../../pojo/SearchCriteria";
import { type Nullable } from "../../pojo/Util";
import { type AdvancedModel } from "./s25ql.search.advanced.criteria.component";
import { Tokenizer } from "./s25ql.tokenizer";

export type ComplexSearch = {
    model: SearchCriteria.Model;
    /**
     * The list of searches this search relies on.
     */
    searches: SearchCriteria.Searches;
};
export type SavedSearch = { chosen: DropDownItem<number>; complex?: ComplexSearch; input?: string };
export type AdvancedSearch = ComplexSearch & { advanced: true; model: AdvancedModel };
export type KeywordCriteriaValue = string | number | { match: "all" | "any"; items: S25ItemI[] };
export type KeywordCriteria = {
    [parameter: string]: KeywordCriteriaValue;
};
export type KeywordSearch = { input?: string; criteria?: KeywordCriteria };
export type QLSearch = { ql: true; input: Tokenizer.QLFormat };
export type Search = QLSearch | KeywordSearch | AdvancedSearch | SavedSearch;

export namespace Search {
    export function isComplex(search?: unknown): search is ComplexSearch {
        return !!search && typeof search === "object" && "model" in search && "searches" in search;
    }
    export function isSaved(search: Nullable<Search>): search is SavedSearch {
        return !!search && "chosen" in search;
    }
    export function isAdvanced(search: Nullable<Search>): search is AdvancedSearch {
        return !!search && "advanced" in search;
    }
    export function isKeyword(search: Nullable<Search>): search is KeywordSearch {
        return !!search && "criteria" in search;
    }
    export function isQl(search: Nullable<Search>): search is QLSearch {
        return !!search && "ql" in search;
    }
}

export namespace Search {
    export const allTypeIds = [
        Item.Ids.Event,
        Item.Ids.Location,
        Item.Ids.Resource,
        Item.Ids.Organization,
        Item.Ids.Task,
        Item.Ids.Contact,
    ] as const;
    export type TypeId = (typeof allTypeIds)[number];
    // TODO: generate `Type` in a better way
    export type Type = "event" | "location" | "resource" | "organization" | "task" | "contact";
    export const allTabValues = ["list", "calendar", "availability", "availabilityWeekly"] as const;
    export type Tab = (typeof allTabValues)[number];
    export type Action = "share" | "publish" | "bulkEdit" | "refresh" | "delete";

    export const eventsInStarredLocations = "&space_favorite=T";

    export const predefined = {
        [Item.Ids.Event]: [
            "&favorite=T",
            "&event_favorite=T",
            eventsInStarredLocations,
            "&resource_favorite=T",
            "&organization_favorite=T",
            "&hot_events=T",
            "&role_id=-1",
            "&role_id=-2",
            "&state=0&role_id=-1",
            "&state=0&role_id=-2",
            "&tasks=T",
        ],
        [Item.Ids.Organization]: ["&favorite=T", "&organization_favorite=T"],
        [Item.Ids.Contact]: ["&favorite=T"],
        [Item.Ids.Location]: ["&favorite=T", "&spaces_favorite=T"],
        [Item.Ids.Resource]: ["&favorite=T", "&resource_favorite=T"],
        [Item.Ids.Task]: ["Overdue", "Outstanding", "Flagged", "Completed", "Assigned_All", "Today_Agenda"],
    } satisfies Record<Search.TypeId, string[]>;

    export const favorite: Partial<Record<Search.TypeId, string>> = {
        [Item.Ids.Event]: "&event_favorite=T",
        [Item.Ids.Organization]: "&organization_favorite=T",
        [Item.Ids.Contact]: "&favorite=T",
        [Item.Ids.Location]: "&spaces_favorite=T",
        [Item.Ids.Resource]: "&resource_favorite=T",
    };
}
